import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Section1 } from "./component/Section1";
import { SideNav } from "./component/SideNav";
import { useLocation, useSearchParams } from "react-router-dom";
import { useResponsiveMobile } from "../../hooks/useResponsiveMobile";
import { setDescription } from "../../seo/seo";
import Banner from "../../component/Banner";

type SectionKey = "payment" | "payService" | "settle" | "merchant" | "sales";

const Slider = () => {};

const Introduction = () => {
  const isMobile = useResponsiveMobile(900);
  const [searchParams] = useSearchParams();
  const anchor = searchParams.get("anchor");
  const { hash } = useLocation();

  const [selected, setSelected] = useState(0);

  //  섹션 ref
  const paymentRef = useRef<HTMLDivElement>(null);
  const payServiceRef = useRef<HTMLDivElement>(null);
  const settleRef = useRef<HTMLDivElement>(null);
  const merchantRef = useRef<HTMLDivElement>(null);
  const salesRef = useRef<HTMLDivElement>(null);

  const sectionRefs: Record<SectionKey, React.RefObject<HTMLDivElement>> = {
    payment: paymentRef,
    payService: payServiceRef,
    settle: settleRef,
    merchant: merchantRef,
    sales: salesRef,
  };

  //  프로그램 스크롤 중 selected 튐 방지
  const isProgrammaticScroll = useRef(false);

  //  모바일 sticky 네비(가로 스크롤) 컨테이너/아이템 ref
  const mobileNavRef = useRef<HTMLDivElement>(null);
  const mobileItemRefs = useRef<Array<HTMLDivElement | null>>([]);

  const mobileNavItems: Array<{
    key: SectionKey;
    label: string;
    icon: string;
  }> = [
    {
      key: "payment",
      label: "결제/정산 내역",
      icon: "/assets/images/icon/sidenav_list.png",
    },
    {
      key: "settle",
      label: "정산 서비스",
      icon: "/assets/images/icon/sidenav_settle.png",
    },
    {
      key: "merchant",
      label: "가맹점 서비스",
      icon: "/assets/images/icon/sidenav_merchant.png",
    },
    {
      key: "payService",
      label: "결제 서비스",
      icon: "/assets/images/icon/sidenav_payment.png",
    },

    {
      key: "sales",
      label: "영업대행 관리 서비스",
      icon: "/assets/images/icon/sidenav_sale.png",
    },
  ];

  const hashToKey: Record<string, SectionKey> = {
    "settlement-history": "payment",
    "payout-service": "settle",
    "merchant-mgmt": "merchant",
    "payment-system": "payService",
    "sales-network": "sales",
  };

  //  헤더 높이 실측
  const getHeaderHeight = (fallback = 80) => {
    const header = document.getElementById("app-header");
    const h = header?.getBoundingClientRect().height;

    return typeof h === "number" && h > 0 ? Math.round(h) : fallback;
  };

  //  모바일 네비 높이 실측(모바일 sticky일 때 섹션이 가려지는 문제 방지)
  const getMobileNavHeight = () => {
    const nav = document.getElementById("mobile-intro-nav");
    const h = nav?.getBoundingClientRect().height;
    return typeof h === "number" && h > 0 ? Math.round(h) : 0;
  };

  //  실제 스크롤 오프셋(헤더 + 모바일 네비)
  const getTopOffset = (fallback = 80) => {
    const headerH = getHeaderHeight(fallback);

    // 모바일일 때만 sticky nav 높이를 더함
    const navH = isMobile ? getMobileNavHeight() : 0;

    const sum = headerH + navH;
    return sum > 0 ? sum : fallback;
  };

  //  offsetTop 기반 스크롤
  const scrollToSection = (key: SectionKey, topOffset = 80) => {
    const el = sectionRefs[key].current;

    if (!el) return;

    isProgrammaticScroll.current = true;

    const y = el.offsetTop - topOffset;

    window.setTimeout(() => {
      window.scrollTo({
        top: y,
        behavior: "smooth",
      });

      isProgrammaticScroll.current = false;
    }, 500);
  };

  //  스크롤에 따라 selected 업데이트
  useEffect(() => {
    const onScroll = () => {
      if (isProgrammaticScroll.current) return;

      const sections: Array<{ idx: number; el: HTMLDivElement | null }> = [
        { idx: 0, el: sectionRefs.payment.current },
        { idx: 1, el: sectionRefs.settle.current },
        { idx: 2, el: sectionRefs.merchant.current },
        { idx: 3, el: sectionRefs.payService.current },
        { idx: 4, el: sectionRefs.sales.current },
      ];

      if (sections.some((s) => !s.el)) return;

      const anchorY = window.innerHeight * 0.8;

      let nextSelected = 0;
      for (const s of sections) {
        const rectTop = s.el!.getBoundingClientRect().top;
        if (rectTop <= anchorY) nextSelected = s.idx;
      }

      setSelected(nextSelected);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //  selected가 바뀌면 모바일 탭이 보이도록 가로 스크롤 정렬
  useEffect(() => {
    if (!isMobile) return;

    const container = mobileNavRef.current;
    const item = mobileItemRefs.current[selected];
    if (!container || !item) return;

    const containerRect = container.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();

    const currentScrollLeft = container.scrollLeft;
    const itemCenter = itemRect.left - containerRect.left + itemRect.width / 2;

    const targetScrollLeft = currentScrollLeft + itemCenter - containerRect.width / 2;

    container.scrollTo({ left: targetScrollLeft, behavior: "smooth" });
  }, [selected]);

  useLayoutEffect(() => {
    const id = hash?.replace("#", "");
    if (!id) return;

    const key = hashToKey[id];
    if (!key) return;

    const tryScroll = (attempt = 0) => {
      const el = sectionRefs[key].current;

      if (el) {
        scrollToSection(key, getTopOffset(80));
        return;
      }
      if (attempt < 60) {
        requestAnimationFrame(() => tryScroll(attempt + 1));
      }
    };

    tryScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, isMobile]);

  useEffect(() => {
    document.title = "품 : Poom | 핵심 기능소개";
    setDescription(
      "가맹점 관리부터 영업대행, 통합 정산 서비스까지! 비즈니스 운영에 필요한 모든 기능을 개발 없이 바로 시작하고 관리 효율을 높여보세요.",
    );
  }, []);

  const headerHeight = getHeaderHeight(80);

  return (
    <div className="w-full flex flex-col">
      {/* 상단 배너 */}
      <section className="w-full flex flex-col pt-[24px] md:pt-[12px] justify-center items-center px-[24px]">
        <Banner
          pcImage="/assets/images/introduction/bg.webp"
          mobileImage="/assets/images/introduction/bg_mobile.webp"
          priority
        >
          <span className="text-white font-bold text-[20px] md:text-display3 text-center">
            품 주요 기능에 대해 <br /> 알려드려요
          </span>
        </Banner>
      </section>

      {/*  모바일 sticky 네비: header 아래에 고정 */}
      {isMobile && (
        <div
          id="mobile-intro-nav"
          className="sticky z-50 bg-white border-b-[1px] border-b-[#DDE0E6] mt-[40px]"
          style={{
            top: headerHeight - 2, // 헤더 아래로 내려서 겹침 방지
          }}
        >
          <div ref={mobileNavRef} className="overflow-x-auto px-4 h-[56px] w-full no-scrollbar">
            <div className="flex flex-row items-center h-full">
              {mobileNavItems.map((item, idx) => {
                const isActive = selected === idx;
                const iconSrc = isActive
                  ? item.icon.replace(/(\.[a-zA-Z0-9]+)$/, "_selected$1")
                  : item.icon;
                return (
                  <div
                    key={item.key}
                    ref={(el) => {
                      mobileItemRefs.current[idx] = el;
                    }}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setSelected(idx);
                      scrollToSection(item.key, getTopOffset(80));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        setSelected(idx);
                        scrollToSection(item.key, getTopOffset(80));
                      }
                    }}
                    className={[
                      "flex flex-row gap-2 items-center h-full shrink-0 px-3 cursor-pointer select-none w-[190px] justify-start",
                      "border-b-2",
                      isActive ? "border-b-[#0079FF]" : "border-b-transparent",
                    ].join(" ")}
                  >
                    <img src={iconSrc} alt="sidenav" width={24} className="h-[24px]" />
                    <span
                      className="font-bold text-[18px] whitespace-nowrap"
                      style={{ color: isActive ? "#0079FF" : "#4D4F58" }}
                    >
                      {item.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* 본문 영역 */}
      <div className="flex flex-col md:flex-row w-full justify-center items-start">
        <div className="flex w-full max-w-[1600px] gap-[12px] xl:py-[160px] pt-[32px] md:pt-[120px] pb-[80px] md:pb-[160px] md:px-[24px]">
          {/* PC 왼쪽 네비 */}
          {!isMobile && (
            <div className="w-full max-w-[300px] flex shrink-0">
              <SideNav
                onSelect={(key) => scrollToSection(key, getTopOffset(80))}
                selected={selected}
                setSelected={setSelected}
                topOffset={getTopOffset(80)}
              />
            </div>
          )}

          {/* 컨텐츠 */}
          <div className="flex w-full">
            <Section1 refs={sectionRefs} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Introduction;
